import {Fragment, Suspense, useMemo} from "react";
import {Navigate, Outlet, Route as RouterRoute, Routes} from "react-router-dom";

import {AuthGuard} from "@/src/modules/auth/components";
import {useStoredMe} from "@/src/modules/core/store/me";
import {LogoutPage} from "@/src/modules/auth/pages";
import {AdminGuard, Loader, PermissionGuard, SupportGuard} from "@/src/modules/core/components";
import {LocationDenied} from "@/src/modules/core/pages";
import {DashboardPage} from "@/src/modules/dashboard/pages";
import DispatchHomePage from "@/src/modules/dispatch/pages/dispatch-home-page";
import HomePage from "@/src/modules/home/pages";
import {AcknowledgeShiftPage} from "@/src/modules/shifts/pages";
import ShiftsManagementGuard from "@/src/modules/shifts/components/shifts-management-guard";

import {
  LazyBriefingBoardsPage,
  LazyCitationsPage,
  LazyCompanyPage,
  LazyCompanyVehicleDetailPage,
  LazyCompanyVehiclesPage,
  LazyDarsPage,
  LazyDirectMessagesPage,
  LazyDispatchCentersPage,
  LazyDispatchLogsPage,
  LazyEmergencyContactsPage,
  LazyGymAccessLogsPage,
  LazyGymAmenitiesPage,
  LazyHelpDebugPage,
  LazyHouseholdsPage,
  LazyIncidentsPage,
  LazyNotificationsPage,
  LazyOfficerOnGuardPage,
  LazyPatrolRoutesPage,
  LazyPermanentParkingCreatePage,
  LazyPermanentParkingPage,
  LazyPermanentParkingTaskDetailPage,
  LazyPoolAccessLogsPage,
  LazyPropertiesPage,
  LazyPropertyDetails,
  LazyPropertyInspectionReportsPage,
  LazyPropertyPoolsPage,
  LazyRbacAdminPage,
  LazyRecreationAccessLogsPage,
  LazyRecreationAmenitiesPage,
  LazyReportsPage,
  LazyResidentClaimPage,
  LazyResidentClaimVerifyPage,
  LazySafelistingsPage,
  LazyShiftDetailsPage,
  LazyShiftHistoryPage,
  LazyShiftsPage,
  LazyStoredVehiclesPage,
  LazySupportPage,
  LazyTaskDetailPage,
  LazyTasksPage,
  LazyTennisAccessLogsPage,
  LazyTennisAmenitiesPage,
  LazyUserDetailPage,
  LazyUsersPage,
  LazyVehicleDetails,
  LazyVehiclesPage,
  LazyViewAsUserPage,
} from "../lib/lazy-route-pages";
import {shouldEnforcePropertySetupGate} from "../lib/me-requires-properties";
import type {Route} from "../types";
import {ICONS} from "../utils/constants";

function DefaultAuthenticatedRedirect() {
  const me = useStoredMe();
  if (shouldEnforcePropertySetupGate(me)) {
    return <Navigate to="/properties" replace />;
  }
  if (me?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/dars" replace />;
}

export const MAIN_ROUTES: Route[] = [
  {
    path: "/dashboard",
    key: "dashboard",
    position: 0.5,
    label: "Dashboard",
    icon: ICONS.dashboard,
    withAuthGuard: true,
    withAdminGuard: true,
    static: true,
    element: <DashboardPage />,
  },
  {
    path: "/home",
    key: "home",
    position: 1,
    label: "Home",
    icon: ICONS.home,
    withAuthGuard: true,
    static: true,
    element: <HomePage />,
  },
  {
    path: "/dispatchdesk",
    key: "dispatchdesk",
    position: 1.05,
    label: "Dispatch desk",
    icon: ICONS.dispatchdesk,
    withAuthGuard: true,
    static: true,
    element: <DispatchHomePage />,
  },
  {
    path: "/help/debug",
    key: "help-debug",
    static: true,
    withAuthGuard: true,
    element: <LazyHelpDebugPage />,
  },
  {
    path: "/dars",
    key: "dars",
    position: 2,
    label: "DAR",
    icon: ICONS.dars,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "dar",
    static: false,
    element: <LazyDarsPage />,
  },
  {
    path: "/incidents",
    key: "incidents",
    position: 3,
    label: "Incidents reports",
    icon: ICONS.incidents,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "incidentreport",
    static: false,
    element: <LazyIncidentsPage />,
  },
  {
    path: "/property-inspection-reports",
    key: "propertyinspectionreport",
    position: 3.5,
    label: "Inspection reports",
    icon: ICONS.propertyinspectionreport,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "propertyinspectionreport",
    static: false,
    element: <LazyPropertyInspectionReportsPage />,
  },
  {
    path: "/poolaccesslogs",
    key: "poolaccesslogs",
    position: 4,
    label: "Pool access logs",
    icon: ICONS.poolaccesslogs,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "amenityaccesslog",
    static: false,
    element: <LazyPoolAccessLogsPage />,
  },
  {
    path: "/tennisaccesslogs",
    key: "tennisaccesslogs",
    position: 4.1,
    label: "Tennis access logs",
    icon: ICONS.tennisaccesslogs,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "amenityaccesslog",
    static: false,
    element: <LazyTennisAccessLogsPage />,
  },
  {
    path: "/gymaccesslogs",
    key: "gymaccesslogs",
    position: 4.2,
    label: "Gym access logs",
    icon: ICONS.gymaccesslogs,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "amenityaccesslog",
    static: false,
    element: <LazyGymAccessLogsPage />,
  },
  {
    path: "/recreationaccesslogs",
    key: "recreationaccesslogs",
    position: 4.3,
    label: "Recreation access logs",
    icon: ICONS.recreationaccesslogs,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "amenityaccesslog",
    static: false,
    element: <LazyRecreationAccessLogsPage />,
  },
  {
    path: "/parking-enforcement",
    key: "vehicles",
    position: 4.5,
    label: "Parking enforcement",
    icon: ICONS.parkingenforcement,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "vehicle",
    static: false,
    element: <LazyVehiclesPage />,
    children: [
      {
        path: ":vehicleId",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "vehicle",
        static: true,
        element: <LazyVehicleDetails />,
      },
    ],
  },
  {
    path: "/dispatchlogs",
    key: "dispatchlogs",
    position: 5,
    label: "Dispatch logs",
    icon: ICONS.dispatchlogs,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "dispatchlog",
    static: false,
    element: <LazyDispatchLogsPage />,
  },
  {
    path: "/shifts-history",
    key: "shifts-history",
    position: 9,
    label: "Shift history",
    icon: ICONS.shifts,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "shift-history",
    static: true,
    element: <LazyShiftHistoryPage />,
  },
];

/** User-scoped nav (sidebar footer); not listed under Management. */
export const USER_FOOTER_ROUTES: Route[] = [
  {
    path: "/direct-messages",
    key: "direct-messages",
    label: "Direct messages",
    icon: ICONS.directmessages,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "directmessage",
    static: true,
    element: <LazyDirectMessagesPage />,
  },
];

export const MANAGEMENT_ROUTES: Route[] = [
  {
    path: "/briefings",
    key: "briefings",
    position: 1,
    label: "Briefing board",
    icon: ICONS.briefings,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "briefingboard",
    static: true,
    element: <LazyBriefingBoardsPage />,
  },
  {
    path: "/reportrequests",
    key: "reportrequests",
    position: 7,
    label: "Reports request",
    icon: ICONS.reportrequests,
    withAuthGuard: true,
    withPermissionGuard: true,
    static: true,
    permission: "reportrequest",
    element: <LazyReportsPage />,
  },
  {
    path: "/tasks",
    key: "tasks",
    position: 3,
    label: "Tasks",
    icon: ICONS.tasks,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "task",
    static: true,
    element: <LazyTasksPage />,
    children: [
      {
        path: ":taskId",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "task",
        static: true,
        element: <LazyTaskDetailPage />,
      },
    ],
  },
  {
    path: "/households",
    key: "households",
    position: 5,
    label: "Households",
    icon: ICONS.households,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "household",
    static: true,
    element: <LazyHouseholdsPage />,
  },
  {
    path: "/pools",
    key: "pools",
    position: 6,
    label: "Pools",
    icon: ICONS.pools,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "property_amenity",
    static: false,
    element: <LazyPropertyPoolsPage />,
  },
  {
    path: "/tennis",
    key: "tennis",
    position: 6.1,
    label: "Tennis courts",
    icon: ICONS.tennis,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "property_amenity",
    static: false,
    element: <LazyTennisAmenitiesPage />,
  },
  {
    path: "/gym",
    key: "gym",
    position: 6.2,
    label: "Gyms",
    icon: ICONS.gym,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "property_amenity",
    static: false,
    element: <LazyGymAmenitiesPage />,
  },
  {
    path: "/recreation",
    key: "recreation",
    position: 6.3,
    label: "Recreation areas",
    icon: ICONS.recreation,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "property_amenity",
    static: false,
    element: <LazyRecreationAmenitiesPage />,
  },
  {
    path: "/permanent-parking",
    key: "permanentparking",
    position: 3,
    label: "Permanent parking",
    icon: ICONS.permanentparking,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "permanentparking",
    static: false,
    element: <LazyPermanentParkingPage />,
    children: [
      {
        path: "new",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "permanentparking",
        static: true,
        element: <LazyPermanentParkingCreatePage />,
      },
      {
        path: ":taskId",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "permanentparking",
        static: true,
        element: <LazyPermanentParkingTaskDetailPage />,
      },
    ],
  },
  {
    path: "/safelistings",
    key: "safelistings",
    position: 3.5,
    label: "Safelistings",
    icon: ICONS.safelistings,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "safelisting",
    static: false,
    element: <LazySafelistingsPage />,
  },
  {
    path: "/stored-vehicles",
    key: "storedvehicles",
    position: 3.55,
    label: "Stored Vehicles",
    icon: ICONS.storedvehicles,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "storedvehicle",
    static: false,
    element: <LazyStoredVehiclesPage />,
  },
  {
    path: "/citations",
    key: "citations",
    position: 3.6,
    label: "Citations",
    icon: ICONS.citations,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "citation",
    static: false,
    element: <LazyCitationsPage />,
  },
];

export const SUPPORT_ROUTES: Route[] = [
  {
    path: "/support",
    key: "support",
    position: 1,
    label: "Support",
    icon: ICONS["view-as-user"],
    withAuthGuard: true,
    withSupportGuard: true,
    static: true,
    element: <LazySupportPage />,
  },
  {
    path: "/support/view-as-user",
    key: "view-as-user",
    position: 1,
    label: "View as user",
    icon: ICONS["view-as-user"],
    withAuthGuard: true,
    withSupportGuard: true,
    static: true,
    element: <LazyViewAsUserPage />,
  },
];

export const SETTINGS_ROUTES: Route[] = [
  {
    path: "/users",
    key: "users",
    position: 1,
    label: "Users",
    icon: ICONS.users,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "user",
    static: true,
    element: <LazyUsersPage />,
    children: [
      {
        path: ":userId",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "user",
        static: true,
        element: <LazyUserDetailPage />,
      },
    ],
  },
  {
    path: "/rbac-admin",
    key: "rbac-admin",
    position: 1.1,
    label: "RBAC",
    icon: ICONS["rbac-admin"],
    withAuthGuard: true,
    withAdminGuard: true,
    static: true,
    element: <LazyRbacAdminPage />,
  },
  {
    path: "/company",
    key: "company",
    position: 4,
    label: "Company",
    icon: ICONS.company,
    withAuthGuard: true,
    withAdminGuard: true,
    static: true,
    element: <LazyCompanyPage />,
  },
  {
    path: "/officer-on-guard",
    key: "officer-on-guard",
    position: 4.125,
    label: "Supervisor on Call",
    icon: ICONS["officer-on-guard"],
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "company",
    static: true,
    element: <LazyOfficerOnGuardPage />,
  },
  {
    path: "/company-vehicles",
    key: "company-vehicles",
    position: 4.25,
    label: "Company vehicles",
    icon: ICONS["company-vehicles"],
    withAuthGuard: true,
    withAdminGuard: true,
    static: true,
    element: <LazyCompanyVehiclesPage />,
    children: [
      {
        path: ":companyVehicleId",
        withAuthGuard: true,
        withAdminGuard: true,
        static: true,
        element: <LazyCompanyVehicleDetailPage />,
      },
    ],
  },
  {
    path: "/properties",
    key: "properties",
    position: 2,
    label: "Properties",
    icon: ICONS.properties,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "property",
    static: true,
    element: <LazyPropertiesPage />,
    children: [
      {
        path: ":propertyId",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "property",
        static: true,
        element: <LazyPropertyDetails />,
      },
    ],
  },
  {
    path: "/dispatch-centers",
    key: "dispatchcenters",
    position: 2.25,
    label: "Dispatch Centers",
    icon: ICONS.dispatchcenters,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "dispatchcenter",
    static: true,
    element: <LazyDispatchCentersPage />,
    children: [
      {
        path: ":id",
        withAuthGuard: true,
        withPermissionGuard: true,
        permission: "dispatchcenter",
        static: true,
        element: <LazyDispatchCentersPage />,
      },
    ],
  },
  {
    path: "/patrol-routes",
    key: "patrolroutes",
    position: 2.26,
    label: "Patrol Routes",
    icon: ICONS.patrolroutes,
    withAuthGuard: true,
    withPermissionGuard: true,
    permission: "patrol-route",
    static: true,
    element: <LazyPatrolRoutesPage />,
  },
  {
    path: "/emergency-contacts",
    key: "emergency-contacts",
    position: 5,
    label: "Emergency Contacts",
    icon: ICONS["emergency-contacts"],
    withAuthGuard: true,
    withAdminGuard: true,
    static: true,
    element: <LazyEmergencyContactsPage />,
  },
  {
    path: "/shifts",
    key: "shifts",
    position: 3,
    label: "Shifts",
    icon: ICONS.shifts,
    withAuthGuard: true,
    withPermissionGuard: true,
    withShiftsManagementGuard: true,
    permission: "shift",
    static: true,
    element: <LazyShiftsPage />,
    children: [
      {
        path: ":shiftId",
        withAuthGuard: true,
        withPermissionGuard: true,
        withShiftsManagementGuard: true,
        permission: "shift",
        static: true,
        element: <LazyShiftDetailsPage />,
      },
    ],
  },
];

const ROUTES: Route[] = [
  {
    path: "/vehicle-maintenance",
    static: true,
    withAuthGuard: true,
    withAdminGuard: true,
    element: <Navigate to="/company-vehicles" replace />,
  },
  {
    path: "/location-denied",
    static: true,
    element: <LocationDenied />,
  },
  {
    path: "logout",
    static: true,
    element: <LogoutPage />,
  },
  {
    path: "/acknowledge-shift",
    static: true,
    element: <AcknowledgeShiftPage />,
  },
  {
    path: "/resident-claim",
    static: true,
    element: <LazyResidentClaimPage />,
  },
  {
    path: "/resident-claim/verify/:token",
    static: true,
    element: <LazyResidentClaimVerifyPage />,
  },
  {
    path: "/notifications",
    static: true,
    withAuthGuard: true,
    element: <LazyNotificationsPage />,
  },
  ...MAIN_ROUTES,
  ...USER_FOOTER_ROUTES,
  ...MANAGEMENT_ROUTES,
  ...SETTINGS_ROUTES,
  ...SUPPORT_ROUTES,
];

type TRouteGuardsProps = {
  route: Route;
};

function RouteGuards({route}: TRouteGuardsProps) {
  let AGuard = route.withAuthGuard ? AuthGuard : Fragment;
  let AdminG = route.withAdminGuard ? AdminGuard : Fragment;
  let SupportG = route.withSupportGuard ? SupportGuard : Fragment;
  let ShiftsG = route.withShiftsManagementGuard ? ShiftsManagementGuard : Fragment;
  let PGuard = useMemo(() => {
    return route.withPermissionGuard
      ? ({children}: {children: React.ReactNode}) => (
          <PermissionGuard
            permission={
              route.key === "officer-on-guard"
                ? "company onguard"
                : `${route.permission} show`
            }
          >
            {children}
          </PermissionGuard>
        )
      : Fragment;
  }, [route]);

  return (
    <AGuard>
      <AdminG>
        <SupportG>
          <PGuard>
            <ShiftsG>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </ShiftsG>
          </PGuard>
        </SupportG>
      </AdminG>
    </AGuard>
  );
}

export default function AuthenticatedRoutes() {
  return (
    <Routes>
      {ROUTES.map((route) => (
        <RouterRoute key={route.path} element={<RouteGuards route={route} />}>
          {route.children ? (
            <RouterRoute path={route.path}>
              {route.children.map((child) => (
                <RouterRoute key={child.path} element={<RouteGuards route={child} />}>
                  <RouterRoute path={child.path} element={child.element} />
                </RouterRoute>
              ))}
              <RouterRoute index element={route.element} />
            </RouterRoute>
          ) : (
            <RouterRoute path={route.path} element={route.element} />
          )}
        </RouterRoute>
      ))}
      <RouterRoute path="*" element={<DefaultAuthenticatedRedirect />} />
    </Routes>
  );
}
